/** How long to wait for a page's heading, which some pages show only once their data loads. */
const WAIT_MS = 3000;
const STEP_MS = 100;

/** The page part of a URL: no query string or fragment. */
export function pagePath(url: string): string {
  return url.split(/[?#]/)[0];
}

/**
 * After a navigation, moves keyboard and screen-reader focus to the new page's
 * heading (the first `selector` match under `root` that isn't one of the old
 * page's, `leaving`, which can still be on screen when the navigation ends) and
 * returns its text for announcing; without this, focus stays on the link that
 * was clicked in the old page. Resolves undefined when no heading appears in
 * time, or `cancelled()` turns true.
 */
export async function focusPageHeading(
  root: ParentNode,
  cancelled: () => boolean = () => false,
  wait = (ms: number) => new Promise((r) => setTimeout(r, ms)),
  leaving: ReadonlySet<Element> = new Set(),
  selector = 'h1',
): Promise<string | undefined> {
  for (let waited = 0; waited <= WAIT_MS; waited += STEP_MS) {
    if (cancelled()) return undefined;
    const heading = Array.from(root.querySelectorAll<HTMLElement>(selector)).find(
      (h) => h.isConnected && !leaving.has(h),
    );
    if (heading) {
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
      // A heading in a layout that Ionic is still showing can't take focus yet: try again.
      if (heading.ownerDocument.activeElement === heading) return heading.textContent?.trim() || undefined;
    }
    await wait(STEP_MS);
  }
  return undefined;
}
